import { useEffect, useRef, useState } from "react";
import CheckCircleDefault from "../assets/li_check-circle-default.svg";
import CheckCircleError from "../assets/li_check-circle-error.svg";
import CheckCircleSuccess from "../assets/li_check-circle-success.svg";
import { useTranslations } from "../hooks/useTranslations.ts";
import { translationKeys } from "../translations/translationKeys.ts";
import Text from "./Text";

type IProps = {
  updateFieldValue: (value: string) => void;
  updateFieldIsValid?: (valid: boolean) => void;
  triggerFieldChange: (value: string) => void;
  label: string;
  placeholder?: string;
  value?: string;
  id?: string;
  name?: string;
  validatemessage?: string;
  fullWidth?: boolean;
  showInstructions?: boolean;
  disableValidation?: boolean;
  readOnly?: boolean;
  required?: boolean;
  email?: string;
  disabled?: boolean;
  onBlur?: () => void;
};

type Rule = {
  id: string;
  translationKey: translationKeys;
  state: "error" | "success" | "default";
  validation: (
    value: string,
    data?: {
      email?: string;
    },
  ) => boolean;
};

const initialRules: Rule[] = [
  {
    id: "length",
    translationKey: translationKeys.PASSWORD_RULE_MIN_LENGTH,
    validation: (value: string) => value.length >= 8 && value.length <= 200,
    state: "default",
  },
  {
    id: "one-lower",
    translationKey: translationKeys.PASSWORD_RULE_ONE_LOWERCASE,
    validation: (value: string) => /[a-z]/.test(value),
    state: "default",
  },
  {
    id: "one-num",
    translationKey: translationKeys.PASSWORD_RULE_ONE_NUMBER,
    validation: (value: string) => /[0-9]/.test(value),
    state: "default",
  },
  {
    id: "no-invalid-symbols",
    translationKey: translationKeys.PASSWORD_RULE_NO_INVALID_SYMBOLS,
    validation: (value: string) =>
      /^[a-zA-Z\d!"£$%^&*()#@~?;:.,{}[\]\-_=+]*$/.test(value), // Taken from demo onboarding
    state: "default",
  },
  {
    id: "not-same-as-email",
    translationKey: translationKeys.PASSWORD_RULE_NOT_EMAIL,
    validation: (value: string, data) => !data?.email || value !== data.email,
    state: "default",
  },
];

function Password(props: IProps) {
  const { updateFieldIsValid } = props;
  const [rules, setRules] = useState<Rule[]>(initialRules);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const { translate } = useTranslations();

  function runValidation(value: string) {
    setRules((prevRules) => {
      return [
        ...prevRules.map((rule) => {
          const ruleClone = { ...rule };
          if (typeof value === "undefined" || value === "") {
            ruleClone.state = "default";
          } else if (
            ruleClone.validation(value, {
              email: props.email,
            })
          ) {
            ruleClone.state = "success";
          } else {
            ruleClone.state = "error";
          }
          return ruleClone;
        }),
      ];
    });
  }

  function handleChangeWithValidation(fn: (value: string) => void) {
    return (value: string) => {
      fn(value);
      if (!props.disableValidation) {
        runValidation(value);
      }
    };
  }

  useEffect(() => {
    // Focus the input when the component mounts
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    if (updateFieldIsValid) {
      updateFieldIsValid(
        props.disableValidation
          ? true
          : rules.every((rule) => rule.state === "success"),
      );
    }
  }, [rules, updateFieldIsValid, props.disableValidation]);

  return (
    <div className={"mb-6"}>
      <Text
        {...props}
        ref={(el) => {
          inputRef.current = el;
        }}
        updateFieldValue={handleChangeWithValidation((e) =>
          props.updateFieldValue(e),
        )}
        triggerFieldChange={handleChangeWithValidation((e) =>
          props.triggerFieldChange(e),
        )}
        type="password"
        noAutoComplete={props.showInstructions}
      />
      {props.showInstructions && (
        <p className="mt-2 text-content-secondary font-semibold">
          {translate(translationKeys.PASSWORD_INSTRUCTIONS_LABEL)}
        </p>
      )}
      {props.showInstructions && (
        <div>
          {rules.map((rule) => (
            <div className="flex items-center" key={rule.id}>
              <div className="w-6 h-6 relative overflow-hidden">
                <img
                  src={CheckCircleDefault}
                  alt="Default"
                  className={`absolute inset-0 transition-all duration-300 ease-in-out w-5 h-5 ${
                    rule.state === "default"
                      ? "opacity-100 scale-100"
                      : "opacity-0 scale-75"
                  }`}
                />
                <img
                  src={CheckCircleSuccess}
                  alt="Success"
                  className={`absolute inset-0 transition-all duration-300 ease-in-out w-5 h-5 ${
                    rule.state === "success"
                      ? "opacity-100 scale-100"
                      : "opacity-0 scale-125"
                  }`}
                />
                <img
                  src={CheckCircleError}
                  alt="Error"
                  className={`absolute inset-0 transition-all duration-300 ease-in-out w-5 h-5 ${
                    rule.state === "error"
                      ? "opacity-100 scale-100"
                      : "opacity-0 scale-75"
                  }`}
                />
              </div>
              <div className="transition-colors duration-300 ease-in-out">
                {translate(rule.translationKey)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default Password;
